import { useState } from "react";
import { Alert, AlertDescription } from "@shared/components/ui/alert";
import { Badge } from "@shared/components/ui/badge";
import { Button } from "@shared/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/components/ui/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@shared/components/ui/collapsible";
import { Separator } from "@shared/components/ui/separator";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Clock,
  AlertCircle,
  FileX,
  CheckSquare,
  XCircle,
  Filter,
  Search,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Input } from "@shared/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/components/ui/select";

const ErrorSummaryCard = ({
  totalPackages,
  validPackages,
  errorPackages,
  onFilterChange,
  onSearchChange,
  searchTerm,
  filterStatus,
}) => {
  const { t } = useTranslation();

  const successRate =
    totalPackages > 0 ? Math.round((validPackages / totalPackages) * 100) : 0;

  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileX className="h-5 w-5" />
          {t("import.packageValidation")}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div className="flex items-center gap-3 p-3 rounded-lg bg-blue-50 dark:bg-blue-950/20">
            <Clock className="h-8 w-8 text-blue-500" />
            <div>
              <p className="text-sm text-muted-foreground">
                {t("import.totalPackages")}
              </p>
              <p className="text-2xl font-bold">{totalPackages}</p>
            </div>
          </div>

          <div className="flex items-center gap-3 p-3 rounded-lg bg-green-50 dark:bg-green-950/20">
            <CheckCircle2 className="h-8 w-8 text-green-500" />
            <div>
              <p className="text-sm text-muted-foreground">
                {t("import.validPackages")}
              </p>
              <p className="text-2xl font-bold text-green-600">
                {validPackages}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3 p-3 rounded-lg bg-red-50 dark:bg-red-950/20">
            <AlertTriangle className="h-8 w-8 text-red-500" />
            <div>
              <p className="text-sm text-muted-foreground">
                {t("import.errorPackages")}
              </p>
              <p className="text-2xl font-bold text-red-600">{errorPackages}</p>
            </div>
          </div>
        </div>

        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">
              {t("import.successRate")}
            </span>
            <span className="text-sm text-muted-foreground">
              {successRate}%
            </span>
          </div>
          <div className="w-full bg-muted rounded-full h-2">
            <div
              className="bg-green-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${successRate}%` }}
            />
          </div>
        </div>

        <Separator className="my-4" />

        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={t("import.searchPackages")}
                value={searchTerm}
                onChange={(e) => onSearchChange(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
          <div className="sm:w-48">
            <Select value={filterStatus} onValueChange={onFilterChange}>
              <SelectTrigger>
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder={t("import.filterByStatus")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("import.allPackages")}</SelectItem>
                <SelectItem value="valid">{t("import.validOnly")}</SelectItem>
                <SelectItem value="errors">{t("import.errorsOnly")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const ErrorDetailsCard = ({ errors, packageId, onFixError }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  if (!errors || errors.length === 0) {
    return (
      <div className="flex items-center gap-2 p-2 bg-green-50 dark:bg-green-950/20 rounded-md">
        <CheckCircle2 className="h-4 w-4 text-green-500" />
        <span className="text-sm text-green-700 dark:text-green-300">
          {t("import.noErrors")}
        </span>
      </div>
    );
  }

  const criticalErrors = errors.filter(
    (error) =>
      error.toLowerCase().includes("required") ||
      error.toLowerCase().includes("missing") ||
      error.toLowerCase().includes("invalid")
  );

  const warningErrors = errors.filter(
    (error) => !criticalErrors.includes(error)
  );

  return (
    <div className="space-y-2">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger asChild>
          <Button variant="ghost" className="w-full justify-between p-2 h-auto">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-red-500" />
              <span className="text-sm font-medium">
                {errors.length}{" "}
                {errors.length === 1 ? t("import.error") : t("import.errors")}
              </span>
              <Badge variant="destructive" className="ml-2">
                {criticalErrors.length}
              </Badge>
              {warningErrors.length > 0 && (
                <Badge variant="secondary">{warningErrors.length}</Badge>
              )}
            </div>
            {isOpen ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </Button>
        </CollapsibleTrigger>

        <CollapsibleContent className="space-y-2">
          {criticalErrors.length > 0 && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                <div className="font-medium mb-2">
                  {t("import.criticalErrors")}:
                </div>
                <ul className="space-y-1">
                  {criticalErrors.map((error, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <XCircle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                      <span className="text-xs">{error}</span>
                    </li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          )}

          {warningErrors.length > 0 && (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                <div className="font-medium mb-2">{t("import.warnings")}:</div>
                <ul className="space-y-1">
                  {warningErrors.map((error, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <AlertTriangle className="h-3 w-3 mt-0.5 flex-shrink-0 text-yellow-500" />
                      <span className="text-xs">{error}</span>
                    </li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          )}

          <div className="flex gap-2 pt-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => onFixError(packageId, "auto")}
              className="text-xs"
            >
              <CheckSquare className="h-3 w-3 mr-1" />
              {t("import.autoFix")}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onFixError(packageId, "manual")}
              className="text-xs"
            >
              {t("import.manualFix")}
            </Button>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
};

export { ErrorSummaryCard, ErrorDetailsCard };
